import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { FlaskConical, MapPin, Star } from "lucide-react";
import Navbar from "../components/Navbar";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 20;

// Annuaire public des laboratoires d'analyses medicales — extension hors
// cahier des charges (comme la page publique du labo, ClinicProfile.js, et
// sa prise de RDV en ligne) : sans cette page, un laboratoire inscrit sur la
// plateforme n'est trouvable que via un lien direct vers /clinic/:slug. Elle
// reutilise GET /api/clinics (deja existant pour les cliniques Pro) avec le
// nouveau filtre establishment_type=laboratory (routes/clinics.py).
const Laboratoires = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const wilaya = searchParams.get("wilaya") || "";
  const [wilayas, setWilayas] = useState([]);
  const [labs, setLabs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/wilayas`).then((res) => setWilayas(res.data || [])).catch(() => setWilayas([]));
  }, []);

  const fetchLabs = useCallback(async (wilayaCode, pageNum) => {
    setLoading(true);
    try {
      const params = { establishment_type: "laboratory", page: pageNum, limit: PAGE_SIZE };
      if (wilayaCode) params.wilaya = wilayaCode;
      const res = await axios.get(`${API_URL}/api/clinics`, { params });
      setLabs(res.data.clinics || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setLabs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    fetchLabs(wilaya, 1);
  }, [wilaya, fetchLabs]);

  const changeWilaya = (code) => {
    if (code) setSearchParams({ wilaya: code });
    else setSearchParams({});
  };

  const changePage = (p) => {
    setPage(p);
    fetchLabs(wilaya, p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <Helmet>
        <title>Trouver un laboratoire d'analyses médicales | Tabbibak</title>
        <meta
          name="description"
          content="Trouvez un laboratoire d'analyses médicales en Algérie et prenez rendez-vous en ligne directement, sans appel téléphonique."
        />
        <link rel="canonical" href="https://tabbibak.com/laboratoires" />
      </Helmet>

      <div className="min-h-screen bg-[#F8FAFC]">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <h1 className="text-2xl font-bold text-[#1A2B3C] mb-2 flex items-center gap-2">
            <FlaskConical className="w-6 h-6 text-[#0596DE]" />
            Trouver un laboratoire d'analyses médicales
          </h1>
          <p className="text-[#64748B] mb-6">
            {loading ? "Chargement..." : `${total} laboratoire(s) trouvé(s)`}
          </p>

          <div className="mb-6">
            <select
              value={wilaya}
              onChange={(e) => changeWilaya(e.target.value)}
              className="w-full sm:w-72 border border-[#E2E8F0] rounded-lg px-4 py-2.5 text-[#1A2B3C] bg-white"
            >
              <option value="">Toutes les wilayas</option>
              {wilayas.map((w) => (
                <option key={w.code} value={w.code}>{w.code} - {w.name_fr}</option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-xl p-6 animate-pulse">
                  <div className="h-6 bg-gray-200 rounded w-1/3 mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-1/4" />
                </div>
              ))}
            </div>
          ) : labs.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center text-[#64748B]">
              Aucun laboratoire trouvé pour cette recherche.
            </div>
          ) : (
            <div className="space-y-4">
              {labs.map((lab) => (
                <div
                  key={lab.clinic_id}
                  className="bg-white rounded-xl border border-[#E2E8F0] p-6 flex items-center justify-between gap-4 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(`/clinic/${lab.slug}`)}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-14 h-14 shrink-0 rounded-xl bg-[#E6F4FB] flex items-center justify-center text-[#0596DE]">
                      <FlaskConical className="w-6 h-6" />
                    </div>
                    <div className="min-w-0">
                      <h2 className="font-semibold text-[#1A2B3C] truncate">{lab.name}</h2>
                      {lab.address && (
                        <p className="text-[#64748B] text-sm flex items-center gap-1 truncate">
                          <MapPin className="w-3.5 h-3.5 shrink-0" /> {lab.address}
                        </p>
                      )}
                      {lab.review_count > 0 && (
                        <p className="text-sm flex items-center gap-1 text-[#1A2B3C]">
                          <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                          {lab.average_rating} <span className="text-[#64748B]">({lab.review_count} avis)</span>
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    className="shrink-0 bg-[#0596DE] text-white px-4 py-2 rounded-lg text-sm hover:bg-[#047CBA] transition-colors"
                    onClick={(e) => { e.stopPropagation(); navigate(`/clinic/${lab.slug}`); }}
                  >
                    Voir la fiche
                  </button>
                </div>
              ))}
            </div>
          )}

          {!loading && pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => changePage(p)}
                  className={`w-9 h-9 rounded-lg text-sm font-medium ${
                    p === page ? "bg-[#0596DE] text-white" : "bg-white border border-[#E2E8F0] text-[#1A2B3C]"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Laboratoires;
